import React from 'react';
import {Button, Empty, Table, Typography} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {useTranslation} from '../../../../hooks/useTranslation';
import {useQuery} from '@tanstack/react-query';
import {apiRequest} from '../../../../utils/AxiosHelper';
import API from '../../../settings/APIs';
import {LossEventRecovery} from '../../../../types/events';

const {Text} = Typography;

const formatCurrency = (value?: number): string => {
  if (value == null) return '—';
  return value.toLocaleString('es-CL', {style: 'currency', currency: 'CLP', maximumFractionDigits: 0});
};

interface FinancialDetailsProps {
  eventId?: string;
  activePanel: string;
}

const FinancialDetails: React.FC<FinancialDetailsProps> = ({eventId}) => {
  const {t} = useTranslation();

  const {data: recoveries, isLoading} = useQuery<LossEventRecovery[]>({
    queryKey: ['events', 'recovery', eventId],
    queryFn: async () => {
      const response = await apiRequest.get(API.events.recovery.list(eventId!));
      return response.data?.data || response.data || [];
    },
    enabled: !!eventId,
    staleTime: 2 * 60 * 1000,
  });

  const rows = recoveries || [];

  const columns = [
    {
      title: t('events.financial.columns.date'),
      dataIndex: 'recoveryDate',
      key: 'recoveryDate',
      width: 120,
    },
    {
      title: t('events.financial.columns.type'),
      dataIndex: 'recoveryType',
      key: 'recoveryType',
      width: 160,
    },
    {
      title: t('events.financial.columns.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      render: (val?: number) => <Text>{formatCurrency(val)}</Text>,
    },
    {
      title: t('events.financial.columns.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  return (
    <div style={{padding: 24, display: 'flex', flexDirection: 'column', gap: 16}}>
      <div style={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button icon={<PlusOutlined/>} size="small" type="primary" ghost>
          {t('events.financial.addRecovery')}
        </Button>
      </div>

      {rows.length === 0 && !isLoading ? (
        <Empty description={t('events.financial.empty')}/>
      ) : (
        <Table
          columns={columns}
          dataSource={rows}
          loading={isLoading}
          rowKey={(r: any) => r.id || Math.random()}
          size="small"
          pagination={false}
        />
      )}
    </div>
  );
};

export default FinancialDetails;
